import {Component, Inject, OnInit} from '@angular/core';
import {Router} from "@angular/router";
import Rollbar from "rollbar";
import {environment} from "../../environments/environment";
import {RollbarService} from "../error-handlers/rollbar";
import {CustomerServiceImpl} from "../services/customer/customer.service.impl";
import {DoorService} from "../services/door/door.service";
import {ScenarioImageService} from "../services/scenario-image/scenario-image.service";
import {UserStorageService} from "../services/user/user.storage.service";

@Component({
  selector: 'app-error-page',
  templateUrl: './error-page.component.html',
  styleUrls: ['./error-page.component.scss'],
})
export class ErrorPageComponent implements OnInit {
  public error: any | null = null;
  public isDev = environment.stage !== 'prod';

  public rollbarEventUUID: string | null = null;

  constructor(
    private router: Router,
    @Inject(RollbarService) private rollbar: Rollbar,
    private doorService: DoorService,
    private scenarioService: ScenarioImageService,
    private userStorageService: UserStorageService
  ) {
    const currentNavigation = this.router.getCurrentNavigation()

    // Prevent direct access to the error page
    if (currentNavigation && !currentNavigation.previousNavigation) {
      console.warn('Direct access to the error page is forbidden, going back to the main page')
      this.router.navigateByUrl('/').then()
    }

    // Read out the error from the router state
    const state = currentNavigation?.extras?.state
    this.error = (state && state['error']) ? state['error'] : null
  }

  translations = new Map<string, { title: string; text: string; button: string; }>();
  currentLanguage = 'en';

  ngOnInit(): void {
    // Gather debug information
    const userId = this.userStorageService.getUserId();
    const customerKey = CustomerServiceImpl.GetCustomerKey();
    const timestamp = new Date().toISOString();
    const currentDoor = this.doorService.getSelectedDoorAtCurrentIndex();
    const currentScenario = this.scenarioService.getSelected();

    // Log the error to Rollbar
    try {
      const rollbarLog = this.rollbar.error('ErrorPage loaded', {
        error: this.error || 'No error provided',
        userId,
        customerKey,
        timestamp,
        doorId: currentDoor?.id,
        doorExternalId: currentDoor?.externalIds[0],
        scenarioId: currentScenario?.id,
        scenarioType: currentScenario?.type
      });
      this.rollbarEventUUID = rollbarLog?.uuid;
    } catch (e) {
      console.warn('Failed to log error to Rollbar', e);
    }

    // Set up mail link
    const mail = 'hello@door-vision.com';
    const mailLinkLocation = this.getDebugMailLink()
    const mailLink = `<a href="${mailLinkLocation}">${mail}</a>`;

    // Initialize translations
    this.translations.set('en', {
      title: 'Unexpected Error',
      text: `An unexpected error occurred. Please try again later.<br>If the problem persists please contact us: ${mailLink}`,
      button: 'Refresh'
    });
    this.translations.set('de', {
      title: 'Unerwarteter Fehler',
      text: `Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.<br>Sollte das Problem weiterhin bestehen, kontaktieren Sie uns bitte: ${mailLink}`,
      button: 'Aktualisieren'
    });
    this.currentLanguage = this.getExistingLanguage();
  }

  getDebugMailLink(): string {
    const userId = this.userStorageService.getUserId();
    const customerKey = CustomerServiceImpl.GetCustomerKey();
    const timestamp = new Date().toISOString();
    const currentDoor = this.doorService.getSelectedDoorAtCurrentIndex();
    const currentScenario = this.scenarioService.getSelected();

    const userAgent = window.navigator.userAgent;
    const platform = window.navigator.platform;
    const screenRes = `${window.screen.width}x${window.screen.height}`;
    const viewport = `${window.innerWidth}x${window.innerHeight}`;
    const language = window.navigator.language;
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const appVersion = environment.version;
    const networkType = (navigator as any)?.connection?.effectiveType ?? 'unknown';
    const hasTouch = 'ontouchstart' in window ? 'yes' : 'no';
    const rollbarRef = this.rollbarEventUUID
      ? `Rollbar Reference: ${this.rollbarEventUUID}\nView in Rollbar: https://rollbar.com/occurrence/uuid/?uuid=${this.rollbarEventUUID}`
      : '';

    const doorInfo = currentDoor
      ? `Door ID: ${currentDoor.id}\nDoor External ID: ${currentDoor.externalIds[0]}`
      : 'No door selected';

    const scenarioInfo = currentScenario
      ? `Scenario ID: ${currentScenario.id}`
      : 'No scenario selected';

    let errorSummary = 'No error object';
    if (this.error) {
      if (typeof this.error === 'object' && 'message' in this.error) {
        errorSummary = this.error.message;
      } else {
        try {
          errorSummary = JSON.stringify(this.error);
        } catch {
          errorSummary = 'Unserializable error object';
        }
      }
    }

    const debugInfo = `
--- Debug Info ---
User ID: ${userId}
Customer: ${customerKey}
Time: ${timestamp}
${doorInfo}
${scenarioInfo}
${rollbarRef}
Error: ${errorSummary}

--- Device Info ---
App Version: ${appVersion}
User Agent: ${userAgent}
Platform: ${platform}
Screen Resolution: ${screenRes}
Viewport: ${viewport}
Language: ${language}
Time Zone: ${timeZone}
Network Type: ${networkType}
Touch Support: ${hasTouch}`

    const subject = encodeURIComponent('[DoorVision] Error Report');

    const body = encodeURIComponent(`Hello DoorVision Team,

I ran into the following problem:
[Please describe what you were doing and what went wrong]

Kind regards,
:\n\n\n${debugInfo}`);
    return `mailto:hello@door-vision.com?subject=${subject}&body=${body}`;
  }

  getLanguages(): Array<string> {
    let browserLang: string[] | undefined = window.navigator?.languages as string[];
    // @ts-expect-error old ts-ignore
    browserLang = browserLang || [window.navigator.language] || [window.navigator.browserLanguage] || [window.navigator.userLanguage] || ['en'];

    // normalize e.g.: en-US to en or de_DE to de
    browserLang = browserLang.map(s => s.split('-')[0]);
    browserLang = browserLang.map(s => s.split('_')[0]);

    return browserLang;
  }

  getExistingLanguage(): string {
    for (const lang of this.getLanguages()) {
      if (this.translations.has(lang)) {
        return lang;
      }
    }
    const [firstKey] = this.translations.keys();
    return firstKey;
  }

  getTitle(): string {
    return this.translations.get(this.currentLanguage)?.title || 'Unexpected Error';
  }

  getText(): string {
    return this.translations.get(this.currentLanguage)?.text || 'An unexpected error occurred'
  }

  getButtonText(): string {
    return this.translations.get(this.currentLanguage)?.button || 'Refresh';
  }

  public onClickRefresh(): void {
    window.location.href = '/';
  }

  getFormattedError(): string {
    if (!this.error) return '';

    let formatted = '';

    // If it's a structured error object with stack
    if (typeof this.error === 'object' && 'stack' in this.error) {
      formatted += `<b>${this.error.name ?? 'Error'}</b>: ${this.error.message ?? ''}<br><br>`;
      formatted += (this.error.stack as string).replace(/\n/g, '<br>').replace(/ /g, '&nbsp;');
    } else {
      // Fallback to stringify
      formatted += JSON.stringify(this.error, null, 2)
        .replace(/\n/g, '<br>')
        .replace(/ /g, '&nbsp;');
    }

    return formatted;
  }
}
